import { useGetListCountriesQuery } from '@/redux/api/api.caller';
import trackingPageSlice, { blacklistingSelector } from '@/redux/features/trackingPage.slice';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, Tag, Text } from '@shopify/polaris';
import { StyledTextField } from '../../styled';
import { useMemo, useState } from 'react';
import { OptionDescriptor } from '@shopify/polaris/build/ts/src/types';
import { checkDirtyField } from '@/helpers';

interface AreasToBeReplacedTextFieldProps {
  isLockedFeature: boolean;
}

const AreasToBeReplacedTextField = ({ isLockedFeature }: AreasToBeReplacedTextFieldProps) => {
  const dispatch = useDispatch();
  const { value: blacklisting, oldValue, fieldsChange } = useSelector(blacklistingSelector);
  const [isFocusAutoComplete, setIsFocusAutoComplete] = useState<boolean>(false);
  const { data, isLoading } = useGetListCountriesQuery(undefined, { skip: !isFocusAutoComplete });

  const transformedOptions: OptionDescriptor[] = useMemo(() => {
    if (data) {
      const initOptions = data.data.map((option) => ({
        value: option,
        label: option,
      }));
      return initOptions.filter((option) =>
        option.value.toLowerCase().includes(blacklisting.areasToBeReplaced.search.toLowerCase()),
      );
    }
    return [];
  }, [blacklisting.areasToBeReplaced.search, data]);

  const handleUpdateText = (value: string) => {
    dispatch(
      trackingPageSlice.actions.handleChangeBlacklistingValue({
        ...blacklisting,
        areasToBeReplaced: { ...blacklisting.areasToBeReplaced, search: value },
      }),
    );
  };

  const handleSelectAutocomplete = (tags: Array<string>) => {
    dispatch(
      trackingPageSlice.actions.handleChangeBlacklistingValue({
        ...blacklisting,
        areasToBeReplaced: { selected: tags, search: '' },
      }),
    );
    const { dirtyField } = checkDirtyField(
      { areasToBeReplaced: oldValue.areasToBeReplaced.selected },
      { areasToBeReplaced: tags },
    );
    dispatch(
      trackingPageSlice.actions.handleChangeBlacklistingFieldsChange({
        ...fieldsChange,
        areasToBeReplaced: dirtyField?.areasToBeReplaced ? dirtyField?.areasToBeReplaced : undefined,
      }),
    );
  };

  const handleRemoveTag = (tag: string) => {
    const removedTag = blacklisting.areasToBeReplaced.selected.filter((option) => option !== tag);
    handleSelectAutocomplete(removedTag);
  };

  const customTextField = (
    <div onFocus={() => setIsFocusAutoComplete(true)}>
      <Autocomplete.TextField
        label={
          <Text variant="headingSm" as="h6" tone={isLockedFeature ? 'subdued' : undefined}>
            Select countries
          </Text>
        }
        placeholder="Country or region to be replaced"
        autoComplete="off"
        value={blacklisting.areasToBeReplaced.search}
        onChange={handleUpdateText}
        disabled={isLockedFeature}
      />
    </div>
  );

  return (
    <StyledTextField>
      <Autocomplete
        listTitle="Suggested Tags"
        allowMultiple={true}
        options={transformedOptions}
        selected={blacklisting.areasToBeReplaced.selected}
        textField={customTextField}
        onSelect={handleSelectAutocomplete}
        loading={isLoading}
      />

      <div className="tag-list">
        {blacklisting.areasToBeReplaced.selected &&
          blacklisting.areasToBeReplaced.selected.length > 0 &&
          blacklisting.areasToBeReplaced.selected.map((tag: string) => (
            <Tag key={tag} onRemove={() => handleRemoveTag(tag)} disabled={isLockedFeature}>
              {tag}
            </Tag>
          ))}
      </div>
    </StyledTextField>
  );
};

export default AreasToBeReplacedTextField;
